import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Star, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CompanyDetectCard } from "@/components/admin/CompanyDetectCard";

function CompanyRow({ c, onSaved }) {
  const [f, setF] = useState({ name: c.name, accountant_email: c.accountant_email || "", threshold: c.approval_threshold ?? "", identifiers: c.identifiers || "" });
  const [busy, setBusy] = useState(false);
  const dirty = f.name !== c.name || f.accountant_email !== (c.accountant_email || "") || String(f.threshold) !== String(c.approval_threshold ?? "") || f.identifiers !== (c.identifiers || "");
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: f.name, accountant_email: f.accountant_email, identifiers: f.identifiers };
      if (f.threshold === "" || f.threshold === null) body.clear_threshold = true; else body.approval_threshold = Number(f.threshold);
      await api.patch(`/admin/companies/${c.id}`, body);
      toast.success("Société enregistrée"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="p-4 grid gap-3 lg:grid-cols-[1.2fr_1.2fr_150px_auto] items-end" data-testid={`company-row-${c.id}`}>
      <label className="text-xs text-slate-500 space-y-1">
        <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />Nom
          <span className="text-slate-400">· {c.files ?? 0} doc. · {c.folders ?? 0} dossiers</span></span>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`company-name-${c.id}`} />
      </label>
      <label className="text-xs text-slate-500 space-y-1"><span>Email du comptable (export mensuel)</span>
        <Input type="email" placeholder="aucun" value={f.accountant_email} onChange={(e) => setF({ ...f, accountant_email: e.target.value })} data-testid={`company-accountant-${c.id}`} />
      </label>
      <label className="text-xs text-slate-500 space-y-1"><span>Seuil direction (€ TTC)</span>
        <Input type="number" min="0" placeholder="général" value={f.threshold} onChange={(e) => setF({ ...f, threshold: e.target.value })} data-testid={`company-threshold-${c.id}`} />
      </label>
      <div className="flex gap-1.5">
        <Button size="sm" onClick={save} disabled={!dirty || busy || !f.name.trim()} data-testid={`company-save-${c.id}`}><Save className="h-4 w-4" /></Button>
        <Button size="sm" variant={c.is_default ? "default" : "outline"} title="Société par défaut (dépôt par email, nouveaux utilisateurs)"
          onClick={() => !c.is_default && act(() => api.patch(`/admin/companies/${c.id}`, { is_default: true }), "Société par défaut modifiée")}
          className={c.is_default ? "bg-amber-600 hover:bg-amber-600" : ""} data-testid={`company-default-${c.id}`}><Star className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" disabled={c.is_default} title="Supprimer (société vide uniquement)"
          onClick={() => window.confirm(`Supprimer la société « ${c.name} » ?`) && act(() => api.delete(`/admin/companies/${c.id}`), "Société supprimée")}
          data-testid={`company-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <label className="text-xs text-slate-500 space-y-1 lg:col-span-4"><span>Indices pour l'IA : SIRET, n° TVA, adresse, autres noms</span>
        <Input placeholder="ex. SIRET 123 456 789 00012 · 12 rue des Pneus 91000 Évry" maxLength={600} value={f.identifiers}
          onChange={(e) => setF({ ...f, identifiers: e.target.value })} data-testid={`company-identifiers-${c.id}`} />
      </label>
    </div>
  );
}

export function AdminCompanies() {
  const { companies, reload } = useCompany();
  const [name, setName] = useState("");
  useEffect(() => { reload(); }, [reload]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/companies", { name }); setName(""); toast.success("Société ajoutée"); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-companies">
      <p className="text-sm text-slate-600 max-w-3xl">Chaque société a ses propres dossiers, fournisseurs, recherche, assistant IA, validations et export comptable.
        Changez de société avec le sélecteur en haut de l'écran. Le seuil vide reprend le seuil général des Paramètres.</p>
      <CompanyDetectCard />
      <div className="bg-white border rounded-xl divide-y">
        {companies.map((c) => <CompanyRow key={`${c.id}-${c.name}-${c.accountant_email}-${c.approval_threshold}-${c.identifiers}`} c={c} onSaved={reload} />)}
      </div>
      <form onSubmit={add} className="flex gap-2 max-w-md">
        <Input placeholder="Nouvelle société" value={name} onChange={(e) => setName(e.target.value)} data-testid="company-new-input" />
        <Button type="submit" disabled={!name.trim()} data-testid="company-add-btn"><Plus className="h-4 w-4 mr-1.5" />Ajouter</Button>
      </form>
    </div>
  );
}
